import React, { useCallback, useEffect, useState } from 'react';
import Icon from './Icon';
import { useAuth } from '../contexts/AuthContext';
import { isSupabaseConfigured } from '../lib/supabase';

// Account-security card: enroll / manage an authenticator-app (TOTP) second
// factor via Supabase Auth MFA. Once a factor is verified, Supabase requires
// it at every subsequent sign-in (the AuthContext + ProtectedRoute enforce the
// aal1→aal2 challenge).
const TwoFactorSetup = () => {
  const { listFactors, enrollTotp, verifyEnrollment, unenrollFactor } = useAuth();

  const [loading, setLoading] = useState(true);
  const [factor, setFactor] = useState(null);        // the verified TOTP factor, if any
  const [enroll, setEnroll] = useState(null);        // { id, totp: { qr_code, secret } } during setup
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOff, setConfirmOff] = useState(false);

  const refresh = useCallback(async () => {
    if (!isSupabaseConfigured) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await listFactors();
      const verified = (data?.totp || []).find((f) => f.status === 'verified') || null;
      setFactor(verified);
    } catch (e) {
      setError(e.message || 'Could not load 2FA status.');
    } finally {
      setLoading(false);
    }
  }, [listFactors]);

  useEffect(() => { refresh(); }, [refresh]);

  const startEnroll = async () => {
    setError(null); setBusy(true);
    try {
      const data = await enrollTotp('Authenticator');
      setEnroll(data);
    } catch (e) {
      setError(e.message || 'Could not start 2FA setup.');
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (e) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      await verifyEnrollment(enroll.id, code.trim());
      setEnroll(null); setCode('');
      await refresh();
    } catch (err) {
      setError(err.message || 'That code didn’t match. Enter the current one from your app.');
    } finally {
      setBusy(false);
    }
  };

  const cancelEnroll = async () => {
    // Roll back the half-finished (unverified) factor so it doesn't linger.
    if (enroll?.id) { try { await unenrollFactor(enroll.id); } catch { /* best effort */ } }
    setEnroll(null); setCode(''); setError(null);
  };

  const turnOff = async () => {
    setError(null); setBusy(true);
    try {
      await unenrollFactor(factor.id);
      setConfirmOff(false);
      await refresh();
    } catch (e) {
      setError(e.message || 'Could not turn off 2FA.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-title">Account security</div>

      {loading ? (
        <div className="text-tx3 text-sm">Checking…</div>
      ) : !isSupabaseConfigured ? (
        <div className="text-tx3 text-sm">Two-factor authentication is unavailable in local mock mode.</div>
      ) : factor && !enroll ? (
        // ── Enabled state ──
        <div className="col col-gap-3">
          <div className="row row-gap-2" style={{ alignItems: 'center' }}>
            <span className="badge badge-success"><Icon name="shield" size={12} /> On</span>
            <span className="text-sm" style={{ color: 'var(--tx2)' }}>
              Two-factor authentication is protecting this account.
            </span>
          </div>
          <div className="text-tx3 text-xs">
            You’ll be asked for a code from your authenticator app each time you sign in.
          </div>
          {!confirmOff ? (
            <button type="button" className="btn btn-outline" style={{ alignSelf: 'flex-start', padding: '8px 14px', fontSize: 12 }} onClick={() => setConfirmOff(true)}>
              Turn off 2FA
            </button>
          ) : (
            <div className="row row-gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="text-sm" style={{ color: 'var(--err)' }}>Turn off two-factor authentication?</span>
              <button type="button" className="btn btn-danger" style={{ padding: '8px 14px', fontSize: 12 }} disabled={busy} onClick={turnOff}>
                {busy ? 'Removing…' : 'Yes, turn off'}
              </button>
              <button type="button" className="btn btn-ghost" style={{ padding: '8px 14px', fontSize: 12 }} onClick={() => setConfirmOff(false)}>Keep on</button>
            </div>
          )}
          {error && <div className="text-xs" style={{ color: 'var(--err)' }}>{error}</div>}
        </div>
      ) : enroll ? (
        // ── Enrollment in progress ──
        <form className="col col-gap-3" onSubmit={confirmEnroll}>
          <div className="text-sm" style={{ color: 'var(--tx2)', lineHeight: 1.6 }}>
            Scan this with your authenticator app (Google Authenticator, 1Password, Authy…), then enter the 6-digit code it shows.
          </div>
          <div className="row row-gap-3" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ background: '#fff', padding: 10, borderRadius: 'var(--r-md)', border: '1px solid var(--bd)' }}>
              {/* Supabase returns qr_code as an SVG data URI */}
              <img src={enroll.totp?.qr_code} alt="2FA QR code" width={160} height={160} style={{ display: 'block' }} />
            </div>
            <div className="col col-gap-2" style={{ minWidth: 180, flex: 1 }}>
              <div className="text-tx3 text-xs">Can’t scan? Enter this key manually:</div>
              <code style={{ fontSize: 12, wordBreak: 'break-all', background: 'var(--bg-input)', padding: '6px 8px', borderRadius: 'var(--r-sm)', color: 'var(--tx1)' }}>
                {enroll.totp?.secret}
              </code>
            </div>
          </div>
          <input
            type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="one-time-code"
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456" autoFocus required
            style={{ width: 160, padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--bd)',
              borderRadius: 'var(--r-md)', color: 'var(--tx1)', fontSize: 18, letterSpacing: '0.3em', textAlign: 'center',
              outline: 'none', fontFamily: 'var(--font-mono, monospace)' }}
          />
          {error && <div className="text-xs" style={{ color: 'var(--err)' }}>{error}</div>}
          <div className="row row-gap-2">
            <button type="submit" className="btn btn-primary" style={{ padding: '8px 16px', fontSize: 13 }} disabled={busy || code.length !== 6}>
              {busy ? 'Verifying…' : 'Verify & enable'}
            </button>
            <button type="button" className="btn btn-ghost" style={{ padding: '8px 14px', fontSize: 13 }} onClick={cancelEnroll}>Cancel</button>
          </div>
        </form>
      ) : (
        // ── Not enrolled ──
        <div className="col col-gap-3">
          <div className="row row-gap-2" style={{ alignItems: 'center' }}>
            <span className="badge badge-muted">Off</span>
            <span className="text-sm" style={{ color: 'var(--tx2)' }}>
              Add an authenticator app for a second layer of sign-in security.
            </span>
          </div>
          <button type="button" className="btn btn-primary" style={{ alignSelf: 'flex-start', padding: '8px 16px', fontSize: 13 }} disabled={busy} onClick={startEnroll}>
            <Icon name="shield" size={14} /> {busy ? 'Starting…' : 'Set up two-factor authentication'}
          </button>
          {error && <div className="text-xs" style={{ color: 'var(--err)' }}>{error}</div>}
        </div>
      )}
    </div>
  );
};

export default TwoFactorSetup;
